import { SUBSCRIPTION_EXPIRED_EVENT } from "@/services/subscription";
import { Button, Modal, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconCrown, IconX } from "@tabler/icons-react";
import moment from "moment-jalaali";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import classes from "./subscription.module.css";
import useSubscription, { toneOf } from "./useSubscription";

const fa = (n: number) => n.toLocaleString("fa-IR");
export const jalali = (iso?: string | null) => (iso ? moment(iso.slice(0, 10), "YYYY-MM-DD").format("jD jMMMM jYYYY") : "—");
const DISMISS_KEY = "subscription-banner-dismissed";

// Header chip: plan and days left; amber at 14 days, red at 3 or once expired.
export function SubscriptionChip() {
  const { subscription: s, available } = useSubscription();
  if (!available || !s) return null;
  const label = s.status === "Active" ? `${fa(Math.max(0, s.daysLeft))} روز مانده` : "منقضی شده";
  return (
    <Link to="/dashboard/subscription" className={classes.chip} data-tone={toneOf(s)}>
      <IconCrown size={15} stroke={1.8} />
      <span>{s.planName} · {label}</span>
    </Link>
  );
}

function dismissedRecently() {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY));
    return at > 0 && Date.now() - at < 24 * 3600_000;
  } catch {
    return false;
  }
}

// Banner from 14 days before the end; once expired it stays (and cannot be dismissed).
export function SubscriptionBanner() {
  const { subscription: s, available } = useSubscription();
  const [hidden, setHidden] = useState(dismissedRecently);
  if (!available || !s) return null;
  const expired = s.status !== "Active";
  if (!expired && (s.daysLeft > 14 || hidden)) return null;

  const text = s.status === "Expired"
    ? "اشتراک شما تمام شده است. ثبت نوبت، ارسال پیامک و نوبت‌دهی آنلاین تا تمدید متوقف است."
    : s.status === "Grace"
      ? `اشتراک شما تمام شده؛ تا ${jalali(s.graceEndDate)} همه‌چیز کار می‌کند. برای قطع نشدن نوبت‌دهی و پیامک‌ها تمدید کنید.`
      : `اشتراک شما تا ${jalali(s.endDate)} اعتبار دارد. برای قطع نشدن نوبت‌دهی و پیامک‌ها، تمدید کنید.`;
  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* storage unavailable: hide for this visit only */ }
    setHidden(true);
  };

  return (
    <div className={classes.banner} data-tone={expired ? "danger" : toneOf(s)} role={expired ? "alert" : "status"}>
      <IconAlertTriangle size={20} stroke={1.8} />
      <p>{text}</p>
      <Button component={Link} to="/dashboard/subscription" size="xs" color={expired ? "red" : "orange"}>تمدید اشتراک</Button>
      {!expired && <button type="button" className={classes.close} aria-label="بستن تا فردا" onClick={dismiss}><IconX size={16} /></button>}
    </div>
  );
}

// Opened when the server refuses an action with SUBSCRIPTION_EXPIRED (see the axios interceptor).
export function RenewModal() {
  const [opened, setOpened] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    const open = () => setOpened(true);
    window.addEventListener(SUBSCRIPTION_EXPIRED_EVENT, open);
    return () => window.removeEventListener(SUBSCRIPTION_EXPIRED_EVENT, open);
  }, []);
  return (
    <Modal opened={opened} onClose={() => setOpened(false)} title="اشتراک تمام شده است" centered>
      <Stack>
        <Text size="sm" lh={1.9}>این کار تا تمدید اشتراک انجام نمی‌شود. اطلاعات سالن سر جایش است و بعد از تمدید همه‌چیز فوراً باز می‌شود.</Text>
        <Button color="violet" onClick={() => { setOpened(false); navigate("/dashboard/subscription"); }}>تمدید اشتراک</Button>
      </Stack>
    </Modal>
  );
}
